import { useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import { api, errText } from "../lib/api";

const input =
  "mt-2 w-full border border-white/20 bg-transparent px-3.5 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:border-ochre focus:outline-none";
const label = "mono block text-[0.55rem] uppercase tracking-[0.16em] text-zinc-400";

const day = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Reference plus the email it was sent from returns the stage. Nothing else is disclosed. */
export const ProposalTracker = () => {
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const look = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.get("/apply/track", { params: { reference, email } });
      setResult(data);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-20" data-testid="proposal-tracker">
      <p className="mono text-[0.55rem] uppercase tracking-[0.2em] text-ochre">Track your proposal</p>
      <h2 className="mt-4 text-base font-semibold text-white md:text-lg">Where your submission has got to</h2>
      <p className="body-copy mt-4 max-w-3xl">
        Enter your reference and the email you used. Nobody else can look it up, and we never publish who got
        in touch.
      </p>

      <form onSubmit={look} className="mt-8 grid max-w-3xl gap-5 sm:grid-cols-[1fr_1fr_auto] sm:items-end" data-testid="tracker-form">
        <label className="block">
          <span className={label}>Reference</span>
          <input value={reference} onChange={(e) => setReference(e.target.value)} required placeholder="KSKF-APP…"
            data-testid="tracker-reference" className={input} />
        </label>
        <label className="block">
          <span className={label}>Email you applied with</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@example.com"
            data-testid="tracker-email" className={input} />
        </label>
        <button type="submit" disabled={busy} className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="tracker-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Check
        </button>
      </form>

      {error ? (
        <p className="mt-6 max-w-2xl border border-maasai/40 bg-maasai/10 p-5 text-sm text-white" data-testid="tracker-error">{error}</p>
      ) : null}

      {result ? (
        <div className="mt-10 max-w-4xl" data-testid="tracker-result">
          <div className="grid gap-px bg-zinc-800 sm:grid-cols-3">
            {[
              ["Reference", result.reference],
              ["County", result.county],
              ["Filed", day(result.filed_at)],
            ].map(([k, v]) => (
              <div key={k} className="bg-[#0b0b0b] p-5">
                <p className="mono text-[0.5rem] uppercase tracking-[0.18em] text-zinc-600">{k}</p>
                <p className="mt-2 text-sm text-white">{v}</p>
              </div>
            ))}
          </div>

          <p className="mono mt-6 inline-block border border-ochre px-4 py-2 text-[0.6rem] uppercase tracking-[0.16em] text-ochre" data-testid="tracker-stage">
            {result.status_label}
          </p>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-zinc-300" data-testid="tracker-note">{result.status_note}</p>

          <ol className="mt-8 space-y-px bg-zinc-800" data-testid="tracker-stages">
            {result.stages.map((s) => (
              <li key={s.id} className="flex items-center gap-3 bg-[#0b0b0b] px-5 py-3.5" data-testid={`tracker-stage-${s.id}`}>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center border ${s.done || s.current ? "border-ochre text-ochre" : "border-zinc-700 text-zinc-700"}`}>
                  {s.done ? <Check size={11} /> : null}
                </span>
                <span className={`text-sm ${s.current ? "text-white" : s.done ? "text-zinc-300" : "text-zinc-600"}`}>
                  {s.label}
                </span>
              </li>
            ))}
          </ol>

          <p className="mono mt-8 max-w-3xl border-l-2 border-zinc-700 bg-[#0b0b0b] p-5 text-[0.58rem] uppercase leading-relaxed tracking-[0.14em] text-zinc-400" data-testid="tracker-no-documents-note">
            Nothing further is needed from you, and no documents should be emailed. No funding, transaction or
            payment is promised at any stage.
          </p>
        </div>
      ) : null}
    </section>
  );
};

export default ProposalTracker;
